import { Head, Link, useForm } from '@inertiajs/react'
import ClientAuthLayout from '../../components/ClientAuthLayout'

interface Props {
  errors: {
    base?: string
  }
  csrf_token?: string
}

export default function ClientLogin({ errors, csrf_token }: Props) {
  const { data, setData, post, processing, transform } = useForm({
    account: {
      email: '',
      password: '',
      remember_me: false,
    },
  })

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    transform((current) => ({ ...current, authenticity_token: csrf_token ?? '' }))
    post('/clients/sign_in')
  }

  return (
    <>
      <Head title="Client Sign In - Pinnacle Navigator" />
      <ClientAuthLayout>
        {errors.base && (
          <div
            className="mb-6 rounded border-l-[3px] border-[#C7161E] bg-[#F9E8E8] p-3"
            role="alert"
          >
            <p className="text-base text-text-primary">{errors.base}</p>
          </div>
        )}

        <div className="mb-8 text-center">
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-text-primary">
            Client access
          </p>
          <h1 className="font-serif text-3xl italic text-text-primary">Welcome back.</h1>
          <p className="mt-3 text-sm leading-6 text-text-primary">
            Sign in to continue to Pinnacle Navigator.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="client-email" className="mb-2 block text-xs font-bold uppercase tracking-[0.1em] text-text-primary">
              Email
            </label>
            <input
              id="client-email"
              type="email"
              value={data.account.email}
              onChange={(event) => setData('account', { ...data.account, email: event.target.value })}
              className="input w-full"
              placeholder="you@example.com"
              required
              autoComplete="email"
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="client-password" className="mb-2 block text-xs font-bold uppercase tracking-[0.1em] text-text-primary">
              Password
            </label>
            <input
              id="client-password"
              type="password"
              value={data.account.password}
              onChange={(event) => setData('account', { ...data.account, password: event.target.value })}
              className="input w-full"
              placeholder="Enter your password"
              required
              autoComplete="current-password"
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3 pt-1">
            <label htmlFor="client-remember-me" className="flex cursor-pointer items-center text-base text-text-primary">
              <input
                id="client-remember-me"
                type="checkbox"
                checked={data.account.remember_me}
                onChange={(event) => setData('account', { ...data.account, remember_me: event.target.checked })}
                className="h-4 w-4 cursor-pointer rounded border-stone text-pinnacle-red focus:ring-charcoal/30"
              />
              <span className="ml-2">Remember me</span>
            </label>
            <Link href="/clients/password/new" className="text-sm font-medium text-text-primary underline underline-offset-4 hover:text-[#C7161E]">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={processing}
            className="w-full rounded-button bg-pinnacle-red px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-pinnacle-red-dark disabled:cursor-not-allowed disabled:opacity-70"
          >
            {processing ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p className="mt-7 text-center text-sm text-text-primary">
          Need help?{' '}
          <a href="mailto:climb@pinnacle.guide" className="font-medium underline underline-offset-4 hover:text-[#C7161E]">
            climb@pinnacle.guide
          </a>
        </p>
      </ClientAuthLayout>
    </>
  )
}